/**
 * SkipToContent — WCAG 2.1 SC 2.4.1 "Bypass Blocks" helper.
 *
 * Keyboard-only users (and screen-reader users navigating without
 * landmarks) need a way to skip past the fixed top nav on every
 * page. Without this, Tab from the URL bar lands on the logo,
 * then 8+ nav links, then login, then the CTA — every load — just
 * to reach the actual page content.
 *
 * The link is visually hidden by default (`sr-only`) and pops into
 * view when it receives keyboard focus (`focus:not-sr-only`). The
 * focused state matches the existing landing-page skip link
 * (MS-pre / Index.tsx) so the brand visual stays consistent across
 * surfaces.
 *
 * Usage: render once as the first child of the page root, BEFORE
 * the nav. Default target is `#main` — every public page should
 * also set `id="main"` on the element wrapping its primary content
 * (typically the <main> tag right after the nav).
 *
 * Why a component rather than copy-paste:
 *   - Single source of truth for the visual treatment.
 *   - Future a11y refinements (different target, custom label,
 *     prefers-reduced-motion handling) live in one file.
 *   - Stops the 7-page-rollout from drifting into 7 slightly-
 *     different skip-link implementations.
 */

interface SkipToContentProps {
  /** Anchor target. Default `#main`. */
  href?: string;
  /** Visible label when focused. Default "Skip to main content". */
  label?: string;
}

export function SkipToContent({
  href = "#main",
  label = "Skip to main content",
}: SkipToContentProps) {
  return (
    <a
      href={href}
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-navy focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
    >
      {label}
    </a>
  );
}
